/* eslint-disable import/extensions   */
import React from "react";
import PropTypes from "prop-types";
import CustomLink from "@/bit/components/next13.components.custom-link";
import Spin from "./spin";
import {
  setLabelAnimation,
  setIconPositionCss,
  composeCssClassNamesAndStyles,
} from "./tools";

import {
  BUTTON_HEIGHT,
  BUTTON_WIDTH,
  BUTTON_TYPE,
  BUTTON_TYPE_HTML,
  BUTTON_TARGET,
  BUTTON_FONT_FAMILY,
  BUTTON_OPERATION,
  ICON_POSITION,
} from "./enums.js";

export {
  BUTTON_HEIGHT,
  BUTTON_WIDTH,
  BUTTON_TYPE,
  BUTTON_TARGET,
  BUTTON_TYPE_HTML,
  BUTTON_FONT_FAMILY,
  BUTTON_OPERATION,
  ICON_POSITION,
};

const Button = ({
  type,
  disabled,
  pill,
  fontFamily,
  forceExternal,
  width,
  height,
  Icon,
  iconProps,
  label,
  href,
  operation,
  segment,
  taggingInfo,
  onClick,
  target,
  buttonTypeHtml,
  className,
  cyData,
}) => {
  if (onClick && !taggingInfo) {
    return null;
  }
  /* COMPLETING TAGGINGINFO IF NEEDED */
  if (onClick && taggingInfo) {
    if (!taggingInfo.componentContent) {
      // eslint-disable-next-line no-param-reassign
      taggingInfo.componentContent = label || "";
    }
    if (!taggingInfo.componentName) {
      // eslint-disable-next-line no-param-reassign
      taggingInfo.componentName = "Button";
    }
  }

  const { cssClassNames, buttonStyles } = composeCssClassNamesAndStyles({
    segment,
    height,
    fontFamily,
    type,
    width,
    pill,
    disabled,
    className,
  });
  let iconPositionCss = null;

  if (Icon && iconProps && iconProps.position) {
    iconPositionCss = setIconPositionCss(iconProps.position);
  }

  const operationIsVisible = operation && operation !== BUTTON_OPERATION.NO;
  const labelAnimationCss = setLabelAnimation(operation);

  return (
    <CustomLink
      taggingInfo={taggingInfo}
      href={href}
      ariaLabel={label}
      onClick={onClick}
      style={buttonStyles}
      buttonTypeHtml={buttonTypeHtml}
      forceExternal={forceExternal}
      buttonParent
      className={cssClassNames}
      target={target}
      disabled={disabled}
      cyData={cyData}
    >
      {!operationIsVisible &&
        Icon &&
        (iconProps.width || iconProps.height) &&
        iconProps.position && (
          <div
            className={`layout-icon ${iconPositionCss || iconProps.position}`}
            id="button-textWithIcon"
          >
            <span className="button-textArea">{label}</span>
            <Icon {...iconProps} className="button-iconArea" />
          </div>
        )}
      {!operationIsVisible && !Icon && (
        <span id="button-textWithoutIcon">{label}</span>
      )}
      {operationIsVisible && (
        <div className={`${labelAnimationCss}`} id="button-operation">
          <Spin operation={operation} spinType={type} />
        </div>
      )}
    </CustomLink>
  );
};

Button.propTypes = {
  /**
   * Información para el tagueado del componente.
   */
  taggingInfo: PropTypes.object.isRequired,
  /**
   * Tema del botón.
   */
  type: PropTypes.string,
  /**
   * Añade la clase css 'pill-shape' para que el botón posea 'border-radious'.
   */
  pill: PropTypes.bool,
  /**
   * Permite definir si el botón está desactivado o no; en caso de desactivado el elemento cambiará de color y dejará de ser clicable.
   */
  disabled: PropTypes.bool,
  /**
   * Tipo de fuente del texto, por defecto es regular.
   */
  fontFamily: PropTypes.string,
  /**
   * Función js que se ejecutará al hacer click, si es necesario.
   */
  onClick: PropTypes.func,
  /**
   * Url a la que se accederá tras hacer click en el elemento si es que lo tuviese.
   */
  href: PropTypes.string,
  /**
   * Valor del target en caso de tener link.
   */
  target: PropTypes.string,
  /**
   * Define si el enlace se tiene que forzar a comportar como enlace externo.
   */
  forceExternal: PropTypes.bool,
  /**
   * Define el ancho del botón.
   */
  width: PropTypes.string,
  /**
   * Altura del botón.
   */
  height: PropTypes.string,
  /**
   * Componente icono ya renderizado.
   */
  Icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * Posicion del icono respecto al texto del botón.
   */
  iconProps: PropTypes.object,
  /**
   * Texto que aparecerá dentro del componente.
   */
  label: PropTypes.string,
  /**
   * Permite establecer una animación en función de un estado a este componente, reflejando una operación de las definidas BUTTON_OPERATION.
   */
  operation: PropTypes.string,
  /**
   * Valor del tipo de button.
   */
  buttonTypeHtml: PropTypes.string,
  /**
   * Define la clase CSS custom que se aplicará al componente.
   */
  className: PropTypes.string,
  cyData: PropTypes.string,
};

Button.defaultProps = {
  pill: false,
  type: BUTTON_TYPE.PRIMARY,
  className: "",
  disabled: false,
  fontFamily: BUTTON_FONT_FAMILY.REGULAR,
  width: "default",
  height: BUTTON_HEIGHT.NORMAL,
  Icon: null,
  iconProps: {
    position: ICON_POSITION.RIGHT,
  },
  label: "",
  operation: BUTTON_OPERATION.NO,
  onClick: null,
  target: "_self",
  buttonTypeHtml: BUTTON_TYPE_HTML.BUTTON,
  forceExternal: false,
  href: "",
  cyData: "",
};
export default Button;
